import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Monitor, Tablet, Smartphone, Eye, Undo2, Redo2, CheckCircle2,
         Loader2, Palette, Type, LayoutGrid, Wand2, ShoppingBag, MessageCircle, Search, Zap,
         History, Coins, ImagePlus, ExternalLink, X, MousePointerClick, Rocket, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import EditorCanvas from "../../editor/EditorCanvas";
import { defaultDoc } from "../../editor/elements";
import BrandLogo from "../../components/BrandLogo";

const DEVICES = { desktop: { w: "100%", icon: Monitor }, tablet: { w: "768px", icon: Tablet }, mobile: { w: "390px", icon: Smartphone } };

const QUICK = [
  { icon: Sparkles, label: "Make Premium", prompt: "Make my website look premium and modern" },
  { icon: Palette, label: "Change Colors", prompt: "Change the website colors to dark green and gold" },
  { icon: Type, label: "Improve Content", prompt: "Improve the website headlines and text to sound more professional" },
  { icon: Smartphone, label: "Fix Mobile", prompt: "Improve the mobile layout and spacing" },
  { icon: LayoutGrid, label: "Add Section", prompt: "Add a testimonials section" },
  { icon: ShoppingBag, label: "Improve Store", prompt: "Make the product section look more professional" },
  { icon: MessageCircle, label: "Add WhatsApp", prompt: "Add a WhatsApp contact button" },
  { icon: Search, label: "Improve SEO", prompt: "Improve headings and content for SEO" },
  { icon: Zap, label: "Simplify", prompt: "Simplify sections and spacing for a cleaner, faster look" },
];

function withContentDefaults(doc, cd) {
  if (!cd || !cd.services) return doc;
  const secs = doc.sections.filter((s) => s.type !== "services" && s.type !== "pricing");
  const idx = secs.findIndex((s) => s.type === "contact" || s.type === "footer");
  const at = idx === -1 ? secs.length : idx;
  secs.splice(at, 0, cd.services, ...(cd.pricing ? [cd.pricing] : []));
  return { ...doc, sections: secs };
}

const CLS_LABEL = { simple: "Simple change", medium: "Medium change", advanced: "Advanced change", major: "Major redesign" };

export default function AiStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [websites, setWebsites] = useState(null);
  const [wid, setWid] = useState("");
  const [data, setData] = useState(null);
  const [storeData, setStoreData] = useState({ products: [], store: null });
  const [doc, setDoc] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [prompt, setPrompt] = useState("");
  const [target, setTarget] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiResp, setAiResp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState("prompt"); // prompt | versions | history
  const [history, setHistory] = useState(null);
  const [mobilePanel, setMobilePanel] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [published, setPublished] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [creditsData, setCreditsData] = useState(null);
  const [buyRef, setBuyRef] = useState({ package_id: "", reference: "", note: "" });
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const estTimer = useRef(null);

  const loadCredits = () => api.get("/store-admin/ai/credits").then((r) => setCreditsData(r.data)).catch(() => {});

  const loadStudio = useCallback(async (id) => {
    const { data: d } = await api.get(`/store-admin/ai-studio/${id}`);
    let seeded = d.draft || d.published;
    if (!seeded || !(seeded.sections || []).length) {
      seeded = withContentDefaults(defaultDoc(d.theme || {}, d.website || {}), d.content_defaults);
      await api.put(`/editor/${id}/draft`, seeded);
    }
    setData(d);
    setDoc(seeded);
    api.get(`/public/store/${d.website.slug}/products`).then((r) => setStoreData({ products: Array.isArray(r.data) ? r.data : [], store: d.website })).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/store-admin/ai-studio").then((r) => {
      setWebsites(r.data.websites);
      const first = r.data.websites.find((w) => w.id === user.website_id) || r.data.websites[0];
      if (first) { setWid(first.id); loadStudio(first.id); }
    }).catch((e) => toast.error(errMsg(e)));
    loadCredits();
  }, [user]);

  useEffect(() => {
    if (!prompt.trim() || !wid) { setEstimate(null); return; }
    clearTimeout(estTimer.current);
    estTimer.current = setTimeout(() => {
      api.post(`/store-admin/ai-studio/${wid}/estimate`, { prompt, section_id: target })
        .then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
    }, 500);
    return () => clearTimeout(estTimer.current);
  }, [prompt, target, wid]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#0c0f0d]"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;
  if (!user) { navigate("/business-owner/login"); return null; }
  if (user.role !== "BUSINESS_OWNER") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div data-testid="ai-studio-403" className="max-w-md text-center space-y-3 rounded-2xl border border-border bg-card p-10">
          <p className="font-display font-bold text-lg">Access Denied</p>
          <p className="text-sm text-muted-foreground">The AI Website Design Studio is available only to Business Owners / Store Owners.</p>
        </div>
      </div>
    );
  }

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true); setAiResp(null);
    try {
      const { data: r } = await api.post(`/store-admin/ai-studio/${wid}/generate`, { prompt, section_id: target });
      setDoc(r.doc); setAiResp(r);
      setData((d) => ({ ...d, credits: r.balance, version_index: r.version_index }));
      toast.success("AI design generated — Draft Saved ✓");
      loadCredits();
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "NO_CREDITS") { toast.error("Not enough AI credits — please top up"); setBuyOpen(true); }
      else if (code === "NEEDS_INIT") { await loadStudio(wid); toast.info("Workspace initialized — try again"); }
      else toast.error(errMsg(e));
    } finally { setGenerating(false); }
  };

  const undoRedo = async (op) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/store-admin/ai-studio/${wid}/${op}`);
      setDoc(r.doc); setData((d) => ({ ...d, version_index: r.version_index }));
      toast.success(op === "undo" ? "Undone" : "Redone");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const restore = async (hid) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/store-admin/ai-studio/${wid}/restore/${hid}`);
      setDoc(r.doc); setData((d) => ({ ...d, version_index: r.version_index }));
      toast.success("Version restored into draft — Publish to make it live");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/store-admin/ai-studio/${wid}/publish`);
      setPublishOpen(false); setPublished(true);
      toast.success("Website updated successfully");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const loadHistory = () => {
    setPanel("history");
    api.get(`/store-admin/ai-studio/${wid}/history`).then((r) => setHistory(r.data.history)).catch(() => setHistory([]));
  };

  const buyCredits = async () => {
    if (!buyRef.package_id) { toast.error("Choose a package first"); return; }
    setBusy(true);
    try {
      await api.post("/store-admin/ai/credits/purchase", buyRef);
      toast.success("Purchase submitted for verification — credits activate after approval");
      setBuyOpen(false); setBuyRef({ package_id: "", reference: "", note: "" });
      loadCredits();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const genImage = async () => {
    if (!imagePrompt.trim()) return;
    setImageBusy(true); setImageUrl("");
    try {
      const { data: r } = await api.post(`/store-admin/ai-studio/${wid}/image`, { prompt: imagePrompt });
      setImageUrl(r.url);
      setData((d) => ({ ...d, credits: r.balance }));
      toast.success("Image generated and saved to Media Library");
      loadCredits();
    } catch (e) {
      if (e?.response?.data?.detail === "NO_CREDITS") { toast.error("Not enough AI credits"); setBuyOpen(true); }
      else toast.error(errMsg(e));
    } finally { setImageBusy(false); }
  };

  const insertImageToHero = async () => {
    const secs = (doc.sections || []).map((s) => s.type === "hero" ? { ...s, props: { ...s.props, image: imageUrl } } : s);
    const nd = { ...doc, sections: secs };
    setDoc(nd);
    await api.put(`/editor/${wid}/draft`, nd);
    toast.success("Image set on Hero — Draft Saved ✓");
  };

  const credits = data?.credits ?? creditsData?.balance ?? 0;
  const versions = data?.versions || [];
  const vIdx = data?.version_index ?? -1;

  const PanelBody = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-extrabold text-lg text-white flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-400" /> AI Website Designer</h2>
            <p className="text-xs text-white/50 mt-0.5">Describe what you want to change.</p>
          </div>
          <button data-testid="ai-credits-balance" onClick={() => setBuyOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 text-xs font-bold hover:bg-emerald-500/25 transition-colors">
            <Coins className="h-3.5 w-3.5" /> {credits} credits
          </button>
        </div>
        <div className="flex gap-1.5 mt-3">
          {[["prompt", Wand2, "Design"], ["versions", History, "Versions"], ["history", History, "History"]].map(([k, Ic, l]) => (
            <button key={k} data-testid={`ai-panel-tab-${k}`} onClick={() => (k === "history" ? loadHistory() : setPanel(k))}
              className={`flex-1 h-8 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 transition-colors ${panel === k ? "bg-emerald-600 text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
              <Ic className="h-3 w-3" /> {l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {panel === "prompt" && (
          <>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 flex items-center gap-1.5"><MousePointerClick className="h-3 w-3" /> Target</label>
              <select data-testid="ai-target-section" value={target} onChange={(e) => setTarget(e.target.value)}
                className="w-full h-10 rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3">
                <option value="">Whole website</option>
                {(doc?.sections || []).filter((s) => !["header", "footer", "spacer", "divider"].includes(s.type)).map((s) => (
                  <option key={s.id} value={s.id}>{s.type} — {(s.props?.heading || s.props?.title || s.props?.text || "").toString().slice(0, 30)}</option>
                ))}
              </select>
            </div>
            <textarea data-testid="ai-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
              placeholder="What would you like to change on your website?"
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white text-sm p-3 placeholder:text-white/30 focus:border-emerald-500/50 focus:outline-none resize-none" />
            <div className="flex flex-wrap gap-1.5">
              {QUICK.map((q) => (
                <button key={q.label} data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setPrompt(q.prompt)}
                  className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[10px] font-bold text-white/70 hover:bg-emerald-500/15 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors">
                  <q.icon className="h-3 w-3" /> {q.label}
                </button>
              ))}
            </div>
            {estimate && prompt.trim() && (
              <div data-testid="ai-estimate" className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold text-white/80">{CLS_LABEL[estimate.cls] || "AI change"}</p>
                  <p className="text-[10px] text-white/40">This change may use approximately {estimate.credits} AI Design Credits</p>
                </div>
                <span className="text-emerald-300 font-display font-extrabold text-lg">-{estimate.credits}</span>
              </div>
            )}
            <button data-testid="ai-generate-btn" onClick={generate} disabled={generating || !prompt.trim()}
              className="w-full h-12 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
              style={{ backgroundImage: "linear-gradient(135deg, #007A4D, #10b981)" }}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {generating ? "Designing..." : "Generate Design"}
            </button>

            {aiResp && (
              <div data-testid="ai-response-card" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-3">
                <p className="text-xs font-bold text-emerald-300 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> AI Design Update</p>
                <p className="text-sm text-white/90">“{aiResp.summary}”</p>
                <div className="space-y-1">
                  {aiResp.changes.map((c, i) => (
                    <p key={i} className="text-[11px] text-white/60 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" /> {c}</p>
                  ))}
                </div>
                <p className="text-[11px] text-white/50">Credits Used: <span className="font-bold text-emerald-300">{aiResp.credits}</span></p>
                <div className="flex gap-2">
                  <button data-testid="ai-resp-undo" onClick={() => undoRedo("undo")} className="flex-1 h-9 rounded-lg bg-white/10 text-white text-xs font-bold hover:bg-white/15">Undo</button>
                  <button data-testid="ai-resp-regenerate" onClick={generate} disabled={generating} className="flex-1 h-9 rounded-lg bg-white/10 text-white text-xs font-bold hover:bg-white/15">Regenerate</button>
                  <button data-testid="ai-resp-publish" onClick={() => setPublishOpen(true)} className="flex-1 h-9 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-500">Publish</button>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 flex items-center gap-1.5"><ImagePlus className="h-3 w-3" /> AI Image ({data?.image_credits ?? 4} credits)</p>
              <div className="flex gap-2">
                <input data-testid="ai-image-prompt" value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="e.g. premium fabric texture banner"
                  className="flex-1 h-10 rounded-lg bg-white/5 border border-white/10 text-white text-xs px-3 placeholder:text-white/30" />
                <button data-testid="ai-image-generate" onClick={genImage} disabled={imageBusy || !imagePrompt.trim()}
                  className="h-10 px-3 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                  {imageBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Create
                </button>
              </div>
              {imageUrl && (
                <div className="space-y-2" data-testid="ai-image-result">
                  <img src={fileUrl(imageUrl)} alt="AI generated" className="w-full h-32 object-cover rounded-lg" />
                  <div className="flex gap-2">
                    <button data-testid="ai-image-hero" onClick={insertImageToHero} className="flex-1 h-8 rounded-lg bg-emerald-600 text-white text-[11px] font-bold">Set as Hero Image</button>
                    <button data-testid="ai-image-copy" onClick={() => { navigator.clipboard?.writeText(fileUrl(imageUrl)); toast.success("Image URL copied"); }}
                      className="h-8 px-3 rounded-lg bg-white/10 text-white text-[11px] font-bold"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {panel === "versions" && (
          <div data-testid="versions-list" className="space-y-2">
            {versions.length === 0 && <p className="text-xs text-white/40 text-center py-8">No AI versions yet — generate your first design.</p>}
            {[...versions].reverse().map((v, ri) => {
              const i = versions.length - 1 - ri;
              return (
                <div key={v.id} className={`rounded-xl border p-3 ${i === vIdx ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-white/90">Version {i + 1}{i === vIdx ? " · current" : ""}</p>
                    <span className="text-[10px] text-white/40">{fmtDateTime(v.at)}</span>
                  </div>
                  <p className="text-[11px] text-white/50 mt-1 line-clamp-2">{v.note}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-emerald-300 font-bold">-{v.credits} credits</span>
                    {i !== vIdx && (
                      <button data-testid={`restore-${v.id}`} onClick={() => restore(v.id)}
                        className="h-7 px-3 rounded-lg bg-white/10 text-white text-[10px] font-bold hover:bg-white/15">Restore This Version</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {panel === "history" && (
          <div data-testid="history-list" className="space-y-2">
            {(history || []).length === 0 && <p className="text-xs text-white/40 text-center py-8">No AI activity yet.</p>}
            {(history || []).map((h) => (
              <div key={h.id} className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-white/90 truncate">{h.summary || h.prompt || h.feature}</p>
                  <p className="text-[10px] text-white/40">{fmtDateTime(h.created_at)} · {h.feature}{h.status === "failed" ? " · failed" : ""}</p>
                </div>
                <span className={`text-xs font-bold shrink-0 ${h.credits_charged ? "text-red-300" : "text-white/30"}`}>
                  {h.credits_charged ? `-${h.credits_charged}` : "0"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="ai-studio" className="h-screen flex flex-col bg-[#0c0f0d] text-white overflow-hidden">
      {/* Header */}
      <header className="h-14 shrink-0 border-b border-white/10 flex items-center gap-3 px-3 sm:px-5">
        <button data-testid="ai-back" onClick={() => navigate("/store")} className="flex items-center gap-1.5 text-xs font-bold text-white/60 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back to Dashboard</span>
        </button>
        <div className="h-5 w-px bg-white/10" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold truncate" data-testid="ai-website-name">{data?.website?.name || "AI Design Studio"}</p>
          <p className="text-[10px] text-white/40 truncate">{data?.theme?.name || ""}</p>
        </div>
        {websites && websites.length > 1 && (
          <select data-testid="ai-website-select" value={wid} onChange={(e) => { setWid(e.target.value); setData(null); setDoc(null); loadStudio(e.target.value); }}
            className="h-9 rounded-lg bg-white/5 border border-white/10 text-xs px-2 max-w-36">
            {websites.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
        <div className="hidden md:flex items-center gap-1 bg-white/5 rounded-lg p-1">
          {Object.entries(DEVICES).map(([k, d]) => (
            <button key={k} data-testid={`device-${k}`} onClick={() => setDevice(k)}
              className={`h-7 w-9 rounded-md flex items-center justify-center transition-colors ${device === k ? "bg-emerald-600 text-white" : "text-white/50 hover:text-white"}`}>
              <d.icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="ai-undo" onClick={() => undoRedo("undo")} disabled={busy || vIdx <= 0} className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30"><Undo2 className="h-4 w-4" /></button>
          <button data-testid="ai-redo" onClick={() => undoRedo("redo")} disabled={busy || vIdx >= versions.length - 1} className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30"><Redo2 className="h-4 w-4" /></button>
          {data?.website?.slug && (
            <a data-testid="ai-preview-live" href={`/site/${data.website.slug}`} target="_blank" rel="noopener noreferrer"
              className="h-9 px-3 rounded-lg bg-white/5 flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-white"><Eye className="h-4 w-4" /> <span className="hidden sm:inline">Preview</span></a>
          )}
          <button data-testid="ai-publish" onClick={() => setPublishOpen(true)}
            className="h-9 px-4 rounded-lg font-bold text-xs text-white flex items-center gap-1.5 active:scale-95 transition-all"
            style={{ backgroundImage: "linear-gradient(135deg, #007A4D, #10b981)" }}>
            <Rocket className="h-3.5 w-3.5" /> Publish
          </button>
        </div>
      </header>

      {/* Workspace */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 overflow-y-auto bg-[#111512] p-3 sm:p-6">
          {!doc ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
          ) : (
            <div data-testid="ai-preview-frame" className="mx-auto bg-white rounded-xl overflow-hidden shadow-2xl transition-all duration-300 min-h-[70vh]"
              style={{ width: DEVICES[device].w, maxWidth: "100%" }}>
              <EditorCanvas doc={doc} theme={data?.theme || {}} store={storeData.store || data?.website || {}}
                info={data?.website?.business_info || {}} branding={data?.branding || {}} products={storeData.products} />
            </div>
          )}
        </div>
        <aside className="hidden lg:flex w-[380px] shrink-0 border-l border-white/10 bg-[#0c0f0d] flex-col" data-testid="ai-panel">
          {PanelBody}
        </aside>
      </div>

      {/* Mobile AI button + drawer */}
      <button data-testid="ai-mobile-open" onClick={() => setMobilePanel(true)}
        className="lg:hidden fixed bottom-5 right-5 z-40 h-14 px-5 rounded-full text-white font-bold text-sm flex items-center gap-2 shadow-2xl"
        style={{ backgroundImage: "linear-gradient(135deg, #007A4D, #10b981)" }}>
        <Sparkles className="h-5 w-5" /> AI Designer
      </button>
      {mobilePanel && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="ai-mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobilePanel(false)} />
          <div className="absolute left-0 right-0 bottom-0 top-16 bg-[#0c0f0d] rounded-t-3xl border-t border-white/10 overflow-hidden flex flex-col">
            <div className="flex justify-end p-2"><button onClick={() => setMobilePanel(false)} className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center"><X className="h-4 w-4" /></button></div>
            <div className="flex-1 min-h-0">{PanelBody}</div>
          </div>
        </div>
      )}

      {/* Publish modal */}
      {publishOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setPublishOpen(false)}>
          <div data-testid="publish-confirm-modal" className="bg-[#131816] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg">Publish Website Changes?</h3>
            <p className="text-sm text-white/60">Your current draft will become the live version of your website.</p>
            <div className="flex gap-3 justify-end">
              <button data-testid="publish-cancel" onClick={() => setPublishOpen(false)} className="h-10 px-5 rounded-full bg-white/10 text-sm font-bold">Cancel</button>
              <button data-testid="publish-confirm" onClick={publish} disabled={busy}
                className="h-10 px-5 rounded-full text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundImage: "linear-gradient(135deg, #007A4D, #10b981)" }}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Published success */}
      {published && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setPublished(false)}>
          <div data-testid="publish-success" className="bg-[#131816] border border-emerald-500/30 rounded-2xl p-8 w-full max-w-md space-y-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center"><CheckCircle2 className="h-7 w-7 text-emerald-400" /></div>
            <h3 className="font-display font-bold text-xl">Website Updated Successfully</h3>
            <p className="text-sm text-white/60">Your latest design is now live.</p>
            <div className="flex gap-3 justify-center">
              {data?.website?.slug && (
                <a data-testid="view-live-website" href={`/site/${data.website.slug}`} target="_blank" rel="noopener noreferrer"
                  className="h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" /> View Live Website
                </a>
              )}
              <button data-testid="continue-designing" onClick={() => setPublished(false)} className="h-10 px-5 rounded-full bg-white/10 text-sm font-bold">Continue Designing</button>
            </div>
          </div>
        </div>
      )}

      {/* Buy credits modal */}
      {buyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setBuyOpen(false)}>
          <div data-testid="buy-credits-modal" className="bg-[#131816] border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Coins className="h-5 w-5 text-emerald-400" /> AI Design Credits</h3>
              <span className="text-xs text-white/50">Balance: <b className="text-emerald-300">{creditsData?.balance ?? credits}</b></span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(creditsData?.packages || []).map((p) => (
                <button key={p.id} data-testid={`pkg-${p.key}`} onClick={() => setBuyRef({ ...buyRef, package_id: p.id })}
                  className={`rounded-xl border p-4 text-left transition-colors ${buyRef.package_id === p.id ? "border-emerald-500 bg-emerald-500/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                  <p className="font-display font-extrabold text-xl">{p.credits} <span className="text-xs font-bold text-white/50">credits</span></p>
                  <p className="text-xs text-white/60">{p.name}</p>
                  <p className="text-sm font-bold text-emerald-300 mt-1">{fmtINR(p.price)}</p>
                </button>
              ))}
            </div>
            {buyRef.package_id && (
              <div className="space-y-3">
                {creditsData?.payment_link && (
                  <a data-testid="credits-pay-link" href={creditsData.payment_link} target="_blank" rel="noopener noreferrer"
                    className="w-full h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2"
                    style={{ backgroundImage: "linear-gradient(135deg, #007A4D, #10b981)" }}>
                    Pay on SuperProfile <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <input data-testid="credits-reference" value={buyRef.reference} onChange={(e) => setBuyRef({ ...buyRef, reference: e.target.value })}
                  placeholder="UTR / Transaction Reference *"
                  className="w-full h-11 rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3 placeholder:text-white/30" />
                <button data-testid="credits-purchase-submit" onClick={buyCredits} disabled={busy}
                  className="w-full h-11 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-50">
                  {busy ? "Submitting..." : "I Have Paid — Submit for Verification"}
                </button>
                <p className="text-[10px] text-white/40 text-center">Credits activate after manual payment verification (normally within 24 hours).</p>
              </div>
            )}
            {(creditsData?.purchases || []).some((p) => p.status === "pending") && (
              <p data-testid="credits-pending-note" className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">A purchase is already under verification.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
